<template>
  <wizard-form v-if="!hidden" v-bind="ouiaProps" next-label="" confirm :title="$t('Riepilogo e preventivo')">
    <pf-alert
      variant="warning"
      v-if="wizard.productsCount === 0"
      :title="$t('Nessun prodotto trovato')"
      class="pf-v6-u-mt-lg"
    >
      <p>{{ $t('Con le opzioni da te selezionate non ci è al momento possibile generare un preventivo.') }}</p>
      <p>
        <i18n-t
          keypath="Puoi provare a modificare le opzioni scelte oppure contattare il nostro supporto commerciale {0}."
          tag="span"
        >
          <router-link :to="{ name: 'tickets' }">
            {{ $t('aprendo un ticket') }}
          </router-link>
        </i18n-t>
      </p>
    </pf-alert>

    <template v-else>
      <wizard-summary-section
        :show-if-empty="wizard.askDomain"
        group="dominio"
        :products="wizard.groupedProducts.dominio.items"
        :currency="wizard.customer?.currency ?? 'EUR'"
        :free-period="wizard.freePeriod"
        :always-full-price="!wizard.fatturaMe"
        :show-qt="false"
        ouia-id="wizard-summary-section-domain"
      >
        <template #summary><wizard-summary-dominio /></template>
      </wizard-summary-section>

      <wizard-summary-section
        group="infrastruttura"
        :products="wizard.groupedProducts.infrastruttura.items"
        :currency="wizard.customer?.currency ?? 'EUR'"
        :free-period="wizard.freePeriod"
        :always-full-price="!wizard.fatturaMe"
        :show-qt="wizard.showQt"
        ouia-id="wizard-summary-section-infrastructure"
      >
        <template #summary><wizard-summary-infrastruttura /></template>
      </wizard-summary-section>

      <wizard-summary-section
        v-if="wizard.askFirewall"
        group="firewall"
        :products="wizard.groupedProducts.firewall.items"
        :currency="wizard.customer?.currency ?? 'EUR'"
        :free-period="wizard.freePeriod"
        :always-full-price="!wizard.fatturaMe"
        :show-qt="wizard.showQt"
        ouia-id="wizard-summary-section-firewall"
      >
        <template #summary><wizard-summary-firewall /></template>
      </wizard-summary-section>

      <wizard-summary-section
        :show-if-empty="wizard.askMail"
        group="mail"
        :products="wizard.groupedProducts.mail.items"
        :currency="wizard.customer?.currency ?? 'EUR'"
        :free-period="wizard.freePeriod"
        :always-full-price="!wizard.fatturaMe"
        :show-qt="false"
        ouia-id="wizard-summary-section-mail"
      >
        <template #summary><wizard-summary-email /></template>
      </wizard-summary-section>

      <wizard-summary-section
        :show-if-empty="wizard.askSsl || wizard.askAddonSla || wizard.askAddonStorage"
        group="addon"
        :products="wizard.groupedProducts.addon.items"
        :currency="wizard.customer?.currency ?? 'EUR'"
        :free-period="wizard.freePeriod"
        :always-full-price="!wizard.fatturaMe"
        show-qt
        ouia-id="wizard-summary-section-addon"
      >
        <template #summary><wizard-summary-addon /></template>
      </wizard-summary-section>

      <pf-flex class="pf-v6-u-mt-2xl" justify-content="flex-end" column-gap="xl">
        <pf-flex-item>
          <wizard-question :title="$t('Titolo')" style="max-width: 500px;">
            <template #description>{{ $t('Se vuoi puoi dare un titolo all\'offerta:') }}</template>
            <x-input
              v-model="wizard.title"
              type="text"
              name="title"
              :placeholder="wizard.defaultTitle"
            />
          </wizard-question>
          <wizard-question :title="$t('Note')" style="max-width: 500px;">
            <template #description>{{ $t('Scrivici ogni tipo di nota o chiarimento relativo al progetto') }}</template>
            <pf-textarea
              v-model="wizard.note"
              name="note"
              placeholder="Note Progetto"
              rows="10"
            />
          </wizard-question>
        </pf-flex-item>

        <pf-flex-item>
          <wizard-question
            v-if="(wizard.askClienti && wizard.customer) ||
              wizard.askDurataFatturazione ||
              (wizard.fatturaMe && !wizard.customer?.currency_locked)"
            :title="$t('Opzioni di fatturazione')"
          >
            <pf-description-list
              horizontal
              class="payment-totals"
              term-width="30ch"
              style="max-width: 600px;"
            >

              <pf-description-list-group v-if="wizard.askClienti && wizard.customer">
                <pf-description-list-term>{{ $t('Offerta intestata a') }}:</pf-description-list-term>
                <pf-description-list-description class="pf-v6-u-text-align-end">
                  <wizard-summary-option
                    :value="wizard.customer.ragione_sociale"
                    edit-step="WizardStepClienti"
                    ouia-id="wizard-summary-fattura-a"
                  />
                </pf-description-list-description>
              </pf-description-list-group>

              <pf-description-list-group v-if="wizard.fatturaMe && wizard.askClienti">
                <pf-description-list-term>{{ $t('Modalità') }}: </pf-description-list-term>
                <pf-description-list-description class="pf-v6-u-text-align-end">
                  <wizard-summary-option
                    :value="wizard.notForResale ? $t('Not for resale') : $t('Rivendita')"
                    edit-step="WizardStepConfigurazione"
                    ouia-id="wizard-summary-notforresale"
                  />
                </pf-description-list-description>
              </pf-description-list-group>

              <pf-description-list-group>
                <pf-description-list-term>{{ $t('Ricorrenza') }}: </pf-description-list-term>
                <pf-description-list-description>
                  <wizard-question-durata v-model="wizard.fatturazioneAnnuale"/>
                </pf-description-list-description>
              </pf-description-list-group>

              <pf-description-list-group v-if="wizard.fatturaMe && !wizard.customer?.currency_locked">
                <pf-description-list-term>{{ $t('Valuta') }}: </pf-description-list-term>
                <pf-description-list-description class="pf-v6-u-text-align-end">
                  <currency-toggle id="currency-dropdown" />
                </pf-description-list-description>
              </pf-description-list-group>

            </pf-description-list>
          </wizard-question>
          <wizard-question :title="$t('Riepilogo dei costi')">
            <div v-if="wizard.freePeriod">
              <strong class="free-label">{{ $t('I primi {0} giorni sono gratuiti', [30]) }}</strong>
            </div>

            <pf-description-list
              v-else
              horizontal
              class="payment-totals"
              term-width="30ch"
              style="max-width: 600px;"
            >
              <pf-description-list-group v-if="wizard.totaleByDurata('Annuale', false)">
                <pf-description-list-term>{{ $t('Totale ricorrente annuale') }}: </pf-description-list-term>
                <pf-description-list-description class="pf-v6-u-text-align-end">
                  <wizard-summary-totals durata="Annuale" ouia-id="wizard-annual-total" big/>
                </pf-description-list-description>
              </pf-description-list-group>

              <pf-description-list-group v-if="wizard.totaleByDurata('Mensile', false)">
                <pf-description-list-term>{{ $t('Totale ricorrente mensile') }}: </pf-description-list-term>
                <pf-description-list-description class="pf-v6-u-text-align-end">
                  <wizard-summary-totals durata="Mensile" ouia-id="wizard-monthly-total" big/>
                </pf-description-list-description>
              </pf-description-list-group>
            </pf-description-list>
          </wizard-question>

          <wizard-summary-actions class="pf-v6-u-mt-2xl"/>
        </pf-flex-item>
      </pf-flex>
    </template>
  </wizard-form>
</template>

<style scoped>
:deep(.pf-v6-c-description-list__text){
    min-width: 0;
    justify-content: flex-end;
    display: flex;
}
</style>

<script lang="ts" setup>
import { useOUIAProps, type OUIAProps } from '@common/ouia';
import { useWizardStep } from '../composition';

import CurrencyToggle from '../../currency-toggle.vue';
import WizardSummarySection from '../summary/wizard-summary-section.vue';
import wizardSummaryOption from '../summary/wizard-summary-option.vue';
import WizardSummaryTotals from '../summary/wizard-summary-totals.vue';
import WizardSummaryActions from '../summary/wizard-summary-actions.vue';
import WizardSummaryDominio from '../summary/wizard-summary-dominio.vue';
import WizardSummaryInfrastruttura from '../summary/wizard-summary-infrastruttura.vue';
import WizardSummaryFirewall from '../summary/wizard-summary-firewall.vue';
import WizardSummaryEmail from '../summary/wizard-summary-email.vue';
import WizardSummaryAddon from '../summary/wizard-summary-addon.vue';
import WizardForm from '../wizard-form.vue';
import WizardQuestion from '../questions/wizard-question.vue';
import WizardQuestionDurata from '../questions/wizard-question-durata.vue';

defineOptions({
  name: 'WizardStepSummary',
});

const props = defineProps<OUIAProps>();
const ouiaProps = useOUIAProps(props);

const { wizard, hidden, isEnabled } = useWizardStep();

defineExpose({
  isEnabled,
});
</script>
